/**
 * Sticky Buy Buttons - Mobile Only
 * Makes both "Add to Cart" and "Buy Now" buttons stick to the bottom when scrolled past
 */

class StickyBuyButton {
  constructor() {
    this.isSticky = false;
    this.init();
  }

  init() {
    // Only run on mobile devices
    if (window.innerWidth > 749) {
      return;
    }

    // Find the original button container
    this.buttonContainer = document.querySelector('.product-form__buttons');
    
    if (!this.buttonContainer) {
      return;
    }

    // Create sticky button wrapper
    this.createStickyButtons();

    // Set up Intersection Observer
    this.setupObserver();

    // Handle window resize
    this.handleResize();
  }

  createStickyButtons() {
    // Find both button sections
    const addToCartSection = this.buttonContainer.querySelector('div:first-child');
    const buyNowSection = this.buttonContainer.querySelector('div:nth-child(2)');
    
    // Create wrapper for sticky buttons
    this.stickyWrapper = document.createElement('div');
    this.stickyWrapper.className = 'sticky-buy-button-wrapper';
    
    // Create inner container for flex layout
    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = 'sticky-buttons-container';
    
    // Clone Add to Cart button
    if (addToCartSection) {
      const addToCartClone = addToCartSection.cloneNode(true);
      addToCartClone.className = 'sticky-button-item';
      buttonsContainer.appendChild(addToCartClone);
      
      // Set up functionality for Add to Cart
      const clonedButton = addToCartClone.querySelector('.product-form__submit');
      if (clonedButton) {
        this.setupAddToCartFunctionality(clonedButton);
      }
    }
    
    // Clone Buy Now button if it exists (dynamic checkout)
    if (buyNowSection && buyNowSection.querySelector('.shopify-payment-button')) {
      const buyNowClone = buyNowSection.cloneNode(true);
      buyNowClone.className = 'sticky-button-item';
      buttonsContainer.appendChild(buyNowClone);
      
      // Set up functionality for Buy Now
      this.setupBuyNowFunctionality(buyNowClone);
    } else if (addToCartSection) {
      // If no Buy Now button, make Add to Cart full width
      buttonsContainer.querySelector('.sticky-button-item').classList.add('full-width');
    }
    
    this.stickyWrapper.appendChild(buttonsContainer);
    document.body.appendChild(this.stickyWrapper);
  }

  setupAddToCartFunctionality(clonedButton) {
    // When sticky Add to Cart button is clicked, trigger the original button
    clonedButton.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Find and click the original button
      const originalButton = document.querySelector('.product-form__buttons .product-form__submit');
      if (originalButton) {
        originalButton.click();
      }
    });
  }

  setupBuyNowFunctionality(buyNowSection) {
    // The Shopify payment button is an iframe, so we need to handle it differently
    // We'll trigger the original buy now button when clicked
    const paymentButton = buyNowSection.querySelector('.shopify-payment-button__button');
    if (paymentButton) {
      paymentButton.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Find and click the original buy now button
        const originalBuyNow = document.querySelector('.product-form__buttons .shopify-payment-button__button');
        if (originalBuyNow) {
          originalBuyNow.click();
        }
      });
    }
  }

  setupObserver() {
    // Create an intersection observer with a threshold
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only show sticky when:
        // 1. Original buttons are NOT intersecting (not visible)
        // 2. The button has left the viewport from the top (boundingClientRect.top < 0)
        const rect = entry.boundingClientRect;
        const hasScrolledPast = rect.top < 0;
        
        if (!entry.isIntersecting && hasScrolledPast) {
          // Original buttons are scrolled past - show sticky buttons
          this.showStickyButtons();
        } else {
          // Original buttons are visible OR haven't been scrolled past yet - hide sticky buttons
          this.hideStickyButtons();
        }
      });
    }, options);

    // Observe the original button container
    if (this.buttonContainer) {
      this.observer.observe(this.buttonContainer);
    }
  }

  showStickyButtons() {
    if (this.stickyWrapper && !this.isSticky) {
      this.isSticky = true;
      this.stickyWrapper.classList.add('is-visible');
    }
  }

  hideStickyButtons() {
    if (this.stickyWrapper && this.isSticky) {
      this.isSticky = false;
      this.stickyWrapper.classList.remove('is-visible');
    }
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // If window is resized to desktop, remove sticky buttons
        if (window.innerWidth > 749) {
          if (this.stickyWrapper) {
            this.stickyWrapper.remove();
          }
          if (this.observer) {
            this.observer.disconnect();
          }
        } else {
          // If resized back to mobile and sticky buttons don't exist, reinitialize
          if (!this.stickyWrapper || !document.body.contains(this.stickyWrapper)) {
            this.init();
          }
        }
      }, 250);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyBuyButton();
  });
} else {
  new StickyBuyButton();
}

// Reinitialize on variant change (for Shopify dynamic variant changes)
document.addEventListener('variant:change', () => {
  // Small delay to ensure DOM has updated
  setTimeout(() => {
    const stickyButton = document.querySelector('.sticky-buy-button-wrapper .product-form__submit');
    const originalButton = document.querySelector('.product-form__buttons .product-form__submit');
    
    if (stickyButton && originalButton) {
      // Update sticky button text and attributes to match original
      stickyButton.innerHTML = originalButton.innerHTML;
      stickyButton.disabled = originalButton.disabled;
      stickyButton.className = originalButton.className;
    }
  }, 100);
});